import React, { useState } from 'react';
import {
    Box,
    IconButton,
    Popover,
    Typography,
    Grid,
    Tooltip,
    Divider,
    Switch,
    FormControlLabel,
    ToggleButtonGroup,
    ToggleButton,
    useTheme
} from '@mui/material';
import {
    Palette as PaletteIcon,
    Check as CheckIcon,
    DarkMode,
    LightMode,
    Settings as SettingsIcon,
    GridView as GridViewIcon,
    FormatListBulleted as TableListIcon
} from '@mui/icons-material';
import { useThemeContext, colorPresets, ColorPresetName, ViewMode } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

interface ThemeCustomizerProps {
    open?: boolean;
    anchorEl?: HTMLElement | null;
    onClose?: () => void;
}

export const ThemeCustomizer: React.FC<ThemeCustomizerProps> = ({
    open: externalOpen,
    anchorEl: externalAnchorEl,
    onClose: externalOnClose
}) => {
    const { mode, toggleColorMode, primaryColor, setPrimaryColor, defaultViewMode, setDefaultViewMode } = useThemeContext();
    const { t } = useLanguage();
    const theme = useTheme();
    const [internalAnchorEl, setInternalAnchorEl] = useState<HTMLElement | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setInternalAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setInternalAnchorEl(null);
        if (externalOnClose) externalOnClose();
    };

    const isControlled = externalOpen !== undefined;
    const open = isControlled ? externalOpen : Boolean(internalAnchorEl);
    const anchorEl = isControlled ? externalAnchorEl : internalAnchorEl;
    const id = open ? 'theme-customizer-popover' : undefined;

    return (
        <>
            {!isControlled && (
                <Tooltip title={t('common.actions.customize') || 'Customize Theme'}>
                    <IconButton
                        color="inherit"
                        onClick={handleOpen}
                        sx={{
                            ml: 1,
                            transition: 'transform 0.3s ease-in-out',
                            '&:hover': {
                                transform: 'rotate(90deg)',
                                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                            }
                        }}
                    >
                        <PaletteIcon />
                    </IconButton>
                </Tooltip>
            )}

            <Popover
                id={id}
                open={Boolean(open)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        width: 320,
                        p: 3,
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                        border: '1px solid',
                        borderColor: 'divider',
                        backdropFilter: 'blur(10px)'
                    }
                }}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: `${theme.palette.primary.main}15`,
                        color: 'primary.main',
                        display: 'flex'
                    }}>
                        <SettingsIcon fontSize="small" />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {t('theme.settings.title') || 'Appearance Settings'}
                    </Typography>
                </Box>

                {/* Mode Switcher */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1 }}>
                        {t('theme.mode.label') || 'Theme Mode'}
                    </Typography>
                    <Box
                        onClick={toggleColorMode}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            borderRadius: 2,
                            cursor: 'pointer',
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            border: '1px solid',
                            borderColor: 'divider',
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {mode === 'dark' ? <DarkMode /> : <LightMode color="warning" />}
                            <Typography variant="body2" fontWeight={500}>
                                {mode === 'dark' ? (t('theme.mode.dark') || 'Dark Mode') : (t('theme.mode.light') || 'Light Mode')}
                            </Typography>
                        </Box>
                        <Switch checked={mode === 'dark'} size="small" />
                    </Box>
                </Box>

                {/* Default Display Mode (Grid / Table) */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1 }}>
                        {t('common.default_view_mode') || 'Default Display Mode'}
                    </Typography>
                    <ToggleButtonGroup
                        value={defaultViewMode}
                        exclusive
                        onChange={(_, val: ViewMode) => val && setDefaultViewMode(val)}
                        fullWidth
                        size="small"
                        sx={{
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            '& .MuiToggleButton-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1,
                                gap: 1,
                                '&.Mui-selected': {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    }
                                }
                            }
                        }}
                    >
                        <ToggleButton value="grid">
                            <GridViewIcon fontSize="small" />
                            {t('common.view_grid') || 'Vue Grille'}
                        </ToggleButton>
                        <ToggleButton value="table">
                            <TableListIcon fontSize="small" />
                            {t('common.view_table') || 'Vue Tableau'}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* Color Presets */}
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1 }}>
                        {t('theme.color.label') || 'Primary Color'}
                    </Typography>
                    <Grid container spacing={1.5}>
                        {Object.entries(colorPresets).map(([key, color]) => (
                            <Grid item xs={3} key={key} display="flex" justifyContent="center">
                                <Tooltip title={key.charAt(0).toUpperCase() + key.slice(1)}>
                                    <Box
                                        onClick={() => setPrimaryColor(key as ColorPresetName)}
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: '50%',
                                            bgcolor: color.main,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s',
                                            border: primaryColor === key ? `3px solid ${theme.palette.background.paper}` : 'none',
                                            boxShadow: primaryColor === key
                                                ? `0 0 0 2px ${color.main}, 0 4px 12px rgba(0,0,0,0.2)`
                                                : '0 2px 4px rgba(0,0,0,0.1)',
                                            '&:hover': {
                                                transform: 'scale(1.1)',
                                            }
                                        }}
                                    >
                                        {primaryColor === key && (
                                            <CheckIcon sx={{ color: 'white', fontSize: 28, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }} />
                                        )}
                                    </Box>
                                </Tooltip>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Popover>
        </>
    );
};
